import { useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { CreditCard, Eye, FileText, Mail, Pencil, Plus, Printer, Receipt, RefreshCw, Repeat, Send, Trash2 } from 'lucide-react';
import type {
  Invoice,
  InvoiceLine,
  InvoicePayment,
  InvoiceTemplate,
  OneTimeCharge,
  PaymentMethod,
  Subscription,
  SubscriptionPlan,
  SubscriptionStatus,
} from '@itr/shared';
import { isCustomerRole, PAYMENT_METHODS, SUBSCRIPTION_STATUSES, formatMoney, formatRelative, serviceEndForDisplay, nextPeriodEnd, type BillingInterval } from '@itr/shared';
import { api } from '../api/client';
import { ChargesTab } from '../components/OneTimeCharges';
import { useAuth } from '../auth/AuthProvider';
import {
  Badge,
  Drawer,
  EmptyState,
  ErrorState,
  RowCheckbox,
  SelectAllCheckbox,
  SelectionBar,
  SkeletonTable,
  autoIntent,
  bulkResultMessage,
  runBulk,
  useDebounced,
  useFetch,
  useSelection,
  useToast,
} from '../components/ui';

type SubRow = Subscription & { planName: string; customerName: string; interval: string };
type InvoiceRow = Invoice & { customerName: string };

export function BillingDocumentsPage() {
  const { can, user } = useAuth();
  const [tab, setTab] = useState<'invoices' | 'subscriptions' | 'plans' | 'charges' | 'templates'>('invoices');

  // Pricing plans and invoice templates are provider-side catalog data — a
  // client's own invoices/subscriptions are already shown, read-only, on
  // their client detail page. This page stays staff-only even if reached by
  // a direct URL rather than the (already-hidden) nav link.
  if (isCustomerRole(user?.role)) return <Navigate to="/" replace />;

  return (
    <>
      <div className="page-header">
        <div className="grow">
          <h1 className="page-title">Invoices & subscriptions</h1>
          <div className="page-subtitle">Recurring plans, what customers owe, and what has been paid.</div>
        </div>
      </div>

      <Summary />

      <div className="row" style={{ gap: 4, marginBottom: 16 }}>
        {(
          [
            ['invoices', 'Invoices', FileText],
            ['subscriptions', 'Subscriptions', Repeat],
            ['plans', 'Plans', Receipt],
            ['charges', 'One-time charges', Receipt],
            ['templates', 'Invoice templates', Mail],
          ] as const
        ).map(([id, label, Icon]) => (
          <button key={id} className={`btn btn-sm${tab === id ? ' btn-primary' : ''}`} onClick={() => setTab(id)}>
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {tab === 'invoices' && <InvoicesTab canWrite={can('billing:write')} />}
      {tab === 'subscriptions' && <SubscriptionsTab canWrite={can('billing:write')} />}
      {tab === 'plans' && <PlansTab canWrite={can('billing:write')} />}
      {tab === 'charges' && <ChargesTab canWrite={can('billing:write')} />}
      {tab === 'templates' && <TemplatesTab canWrite={can('billing:write')} />}
    </>
  );
}

function Summary() {
  const { data } = useFetch(
    () =>
      api.get<{
        mrrMinor: number;
        active: number;
        currency: string;
        invoices: { outstandingMinor: number; overdueMinor: number; overdueCount: number; draftCount: number };
      }>('/api/billing/subscriptions/summary'),
    [],
    { refreshMs: 60_000 },
  );
  if (!data) return null;

  return (
    <div className="stat-strip">
      <div className="stat-chip">
        <div className="stat-chip-label">Monthly recurring</div>
        <div className="stat-chip-value">{formatMoney(data.mrrMinor, data.currency)}</div>
      </div>
      <div className="stat-chip">
        <div className="stat-chip-label">Active subscriptions</div>
        <div className="stat-chip-value">{data.active}</div>
      </div>
      <div className="stat-chip">
        <div className="stat-chip-label">Outstanding</div>
        <div className="stat-chip-value">{formatMoney(data.invoices.outstandingMinor, data.currency)}</div>
      </div>
      <div className="stat-chip">
        <div className="stat-chip-label">Overdue</div>
        <div
          className="stat-chip-value"
          style={{ color: data.invoices.overdueCount > 0 ? 'var(--itr-critical)' : undefined }}
        >
          {formatMoney(data.invoices.overdueMinor, data.currency)}
        </div>
      </div>
    </div>
  );
}

/* ================================================================== */
/* Invoices                                                           */
/* ================================================================== */

function InvoicesTab({ canWrite }: { canWrite: boolean }) {
  const navigate = useNavigate();
  const toast = useToast();
  const [state, setState] = useState('');
  const [generating, setGenerating] = useState(false);

  const invoices = useFetch(() => api.get<InvoiceRow[]>('/api/billing/invoices', { state: state || undefined }), [state]);
  const rows = invoices.data ?? [];

  // Only an unpaid invoice can be deleted (the server enforces this too, and
  // also refuses a part-paid one) — restricting what's selectable is honester
  // than offering a checkbox on a paid invoice that can only fail.
  const deletableIds = rows.filter((inv) => inv.state !== 'paid').map((inv) => inv.id);
  const selection = useSelection(deletableIds);

  const bulkDelete = async () => {
    const issued = rows.filter((inv) => selection.selectedIds.includes(inv.id) && inv.state !== 'draft').length;
    const warning = issued
      ? `\n\n${issued} of them ${issued === 1 ? 'has' : 'have'} been issued: the client may already have it, and its number will be missing from the sequence. Voiding keeps the record instead.`
      : '';
    if (!confirm(`Delete ${selection.count} invoice(s)? This cannot be undone.${warning}`)) return;
    const result = await runBulk(selection.selectedIds, (id) => api.delete(`/api/billing/invoices/${id}`));
    const { message, intent } = bulkResultMessage('Deleted', result);
    toast(message, intent);
    selection.clear();
    invoices.reload();
  };

  return (
    <>
      <div className="row" style={{ marginBottom: 12, gap: 4, flexWrap: 'wrap' }}>
        {['', 'draft', 'issued', 'paid', 'overdue', 'void'].map((s) => (
          <button key={s || 'all'} className={`btn btn-sm${state === s ? ' btn-primary' : ''}`} onClick={() => setState(s)}>
            {s || 'All'}
          </button>
        ))}
        <span className="grow" />
        {canWrite && (
          <button className="btn btn-primary" onClick={() => setGenerating(true)}>
            <Plus size={15} /> Generate invoice
          </button>
        )}
      </div>

      {canWrite && deletableIds.length > 0 && (
        <SelectionBar selection={selection}>
          <button className="btn btn-sm btn-danger" onClick={bulkDelete}>
            <Trash2 size={12} /> Delete
          </button>
        </SelectionBar>
      )}

      <div className="card">
        {invoices.loading ? (
          <div style={{ padding: 16 }}>
            <SkeletonTable rows={5} cols={5} />
          </div>
        ) : invoices.error ? (
          <ErrorState error={invoices.error} onRetry={invoices.reload} />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<FileText size={20} />}
            title="No invoices"
            text="Generate one for a customer to bill their subscriptions and metered usage for a period."
          />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  {canWrite && deletableIds.length > 0 && (
                    <th style={{ width: 40 }}>
                      <SelectAllCheckbox selection={selection} />
                    </th>
                  )}
                  <th style={{ width: 140 }}>Number</th>
                  <th>Customer</th>
                  <th style={{ width: 180 }}>Service period</th>
                  <th data-align="right" style={{ width: 120 }}>
                    Total
                  </th>
                  <th style={{ width: 100 }}>State</th>
                  <th data-align="right" style={{ width: 120 }}>
                    Due
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((inv) => (
                  <tr key={inv.id} data-clickable="true" onClick={() => navigate(`/invoices/${inv.id}`)}>
                    {canWrite && deletableIds.length > 0 && (
                      <td>{inv.state !== 'paid' && <RowCheckbox selection={selection} id={inv.id} />}</td>
                    )}
                    <td className="mono">{inv.number}</td>
                    <td style={{ fontWeight: 550 }}>{inv.customerName}</td>
                    <td className="subtle" style={{ fontSize: 12 }}>
                      {serviceRange(inv.periodStart, inv.periodEnd)}
                    </td>
                    <td data-align="right" className="nums" style={{ fontWeight: 600 }}>
                      {formatMoney(inv.totalMinor, inv.currency)}
                    </td>
                    <td>
                      <Badge intent={autoIntent(inv.state)}>{inv.state}</Badge>
                    </td>
                    <td data-align="right" className="subtle" style={{ fontSize: 12 }}>
                      {inv.dueAt ? formatRelative(inv.dueAt) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Drawer open={generating} onClose={() => setGenerating(false)} title="Generate an invoice">
        <GenerateForm
          onDone={(invoice) => {
            setGenerating(false);
            invoices.reload();
            if (invoice.state === 'draft') {
              toast('Invoice generated as a draft', 'good');
            } else if (invoice.sendError) {
              toast('Invoice issued, but the email could not be delivered', 'warn', invoice.sendError);
            } else {
              toast('Invoice issued and emailed to the client', 'good');
            }
            navigate(`/invoices/${invoice.id}`);
          }}
        />
      </Drawer>
    </>
  );
}

function GenerateForm({
  onDone,
  initial,
}: {
  onDone: (invoice: Invoice) => void;
  /** Pre-fills — and locks — the customer/period from a specific subscription, so billing it doesn't require re-picking what's already known. */
  initial?: { customerId: string; periodStart: number; periodEnd: number; currency?: string };
}) {
  const toast = useToast();
  const { data: customers } = useFetch(() => api.get<Array<{ id: string; name: string }>>('/api/customers'), []);
  const { data: templates } = useFetch(() => api.get<InvoiceTemplate[]>('/api/billing/invoice-templates'), []);

  // Default to last calendar month, which is what an operator billing on the
  // 1st almost always wants.
  const lastMonth = new Date();
  lastMonth.setUTCDate(1);
  const periodEnd = lastMonth.getTime();
  lastMonth.setUTCMonth(lastMonth.getUTCMonth() - 1);
  const periodStart = lastMonth.getTime();

  const [customerId, setCustomerId] = useState(initial?.customerId ?? '');
  const [from, setFrom] = useState(isoDate(initial?.periodStart ?? periodStart));
  const [to, setTo] = useState(isoDate(initial?.periodEnd ?? periodEnd));
  // Net-30 from the period end is the common default; still editable per
  // invoice for a negotiated term or a no-due-date case (cleared to blank).
  const [dueDate, setDueDate] = useState(isoDate((initial?.periodEnd ?? periodEnd) + 30 * 86_400_000));
  // Billing cycle: pick Daily/Weekly/Monthly/Annual to fill the period end (and
  // due date) from the start, or Custom to type both dates by hand (the old way).
  const [cycle, setCycle] = useState<BillingInterval | 'custom'>('custom');
  // Apply a cycle to the given start: period end via the shared nextPeriodEnd,
  // due date net-30 from that end.
  const applyCycle = (c: BillingInterval | 'custom', fromStr: string) => {
    setCycle(c);
    if (c === 'custom' || !fromStr) return;
    const startMs = Date.parse(fromStr);
    if (Number.isNaN(startMs)) return;
    const endMs = nextPeriodEnd(startMs, c);
    setTo(isoDate(endMs));
    setDueDate(isoDate(endMs + 30 * 86_400_000));
  };
  const [includeUsage, setIncludeUsage] = useState(true);
  const [taxPct, setTaxPct] = useState('0');
  const [templateId, setTemplateId] = useState('');
  const [autoSend, setAutoSend] = useState(true);
  const [busy, setBusy] = useState(false);
  // One-off lines for this invoice only; pending one-time charges (support
  // fees etc.) are added by the server on top of these.
  const [extras, setExtras] = useState<Array<{ description: string; quantity: string; price: string }>>([]);
  const pendingCharges = useFetch(
    () => api.get<OneTimeCharge[]>('/api/billing/charges', { customerId, state: 'pending' }),
    [customerId],
    { enabled: Boolean(customerId) },
  );
  // Blank bills in the client's account currency. Offered choices are the
  // currencies this client actually has subscriptions or pending charges in.
  const [currency, setCurrency] = useState(initial?.currency ?? '');
  const customerSubs = useFetch(
    () => api.get<Subscription[]>('/api/billing/subscriptions', { customerId }),
    [customerId],
    { enabled: Boolean(customerId) },
  );
  const currencyOptions = [
    ...new Set([...(customerSubs.data ?? []).map((s) => s.currency), ...(pendingCharges.data ?? []).map((c) => c.currency)]),
  ].sort();
  const pending = customerId
    ? (pendingCharges.data ?? []).filter((c) => !currency || c.currency === currency.toUpperCase())
    : [];

  const setExtra = (i: number, key: 'description' | 'quantity' | 'price', value: string) =>
    setExtras((rows) => rows.map((row, j) => (j === i ? { ...row, [key]: value } : row)));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      const invoice = await api.post<Invoice>('/api/billing/invoices/generate', {
        extraLines: extras
          .filter((x) => x.description.trim())
          .map((x) => ({
            description: x.description.trim(),
            quantity: Number(x.quantity) || 1,
            unitPriceMinor: Math.round(Number(x.price) * 100) || 0,
          })),
        customerId,
        periodStart: Date.parse(from),
        periodEnd: Date.parse(to),
        includeUsage,
        taxPct: Number(taxPct) || 0,
        templateId: templateId || null,
        autoSend,
        dueAt: dueDate ? Date.parse(dueDate) : null,
        ...(currency.trim() ? { currency: currency.trim().toUpperCase() } : {}),
      });
      onDone(invoice);
    } catch (err) {
      toast('Could not generate the invoice', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <div className="field">
        <label className="field-label">Customer</label>
        {initial ? (
          <input className="input" disabled value={(customers ?? []).find((c) => c.id === customerId)?.name ?? '…'} />
        ) : (
          <select className="select" required value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
            <option value="">Select…</option>
            {(customers ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        )}
      </div>

      <div className="row" style={{ gap: 8 }}>
        <div className="field grow">
          <label className="field-label">Billing cycle</label>
          <select className="select" value={cycle} onChange={(e) => applyCycle(e.target.value as BillingInterval | 'custom', from)}>
            <option value="custom">Custom (pick dates)</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="yearly">Annual</option>
          </select>
        </div>
        <div className="field grow">
          <label className="field-label">Period start</label>
          <input
            className="input"
            type="date"
            value={from}
            onChange={(e) => {
              setFrom(e.target.value);
              applyCycle(cycle, e.target.value);
            }}
            required
          />
        </div>
        <div className="field grow">
          <label className="field-label">Period end</label>
          <input className="input" type="date" value={to} onChange={(e) => setTo(e.target.value)} required disabled={cycle !== 'custom'} />
        </div>
      </div>

      <div className="row" style={{ gap: 8 }}>
        <div className="field grow">
          <label className="field-label">Due date</label>
          <input className="input" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
          <span className="field-help">Leave blank for no fixed due date.</span>
        </div>
        <div className="field grow">
          <label className="field-label">Tax %</label>
          <input className="input" type="number" min={0} max={100} step="0.1" value={taxPct} onChange={(e) => setTaxPct(e.target.value)} />
        </div>
        <div className="field" style={{ width: 110 }}>
          <label className="field-label">Currency</label>
          <input
            className="input"
            list="invoice-currencies"
            maxLength={3}
            pattern="[A-Za-z]{3}"
            placeholder="Account"
            value={currency}
            onChange={(e) => setCurrency(e.target.value.toUpperCase())}
          />
          <datalist id="invoice-currencies">
            {currencyOptions.map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
        </div>
      </div>
      {currencyOptions.length > 1 && (
        <p className="subtle" style={{ fontSize: 12.5, marginTop: -6, marginBottom: 12 }}>
          This client is billed in {currencyOptions.join(' and ')}. An invoice holds one currency — items in the others stay
          for an invoice in that currency.
        </p>
      )}

      {(templates ?? []).length > 0 && (
        <div className="field">
          <label className="field-label">Letter format</label>
          <select className="select" value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
            <option value="">Default{(templates ?? []).find((t) => t.isDefault) ? ` — ${(templates ?? []).find((t) => t.isDefault)!.name}` : ''}</option>
            {(templates ?? []).map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="field">
        <label className="field-label">One-time charges</label>
        {pending.length > 0 ? (
          <div className="subtle" style={{ fontSize: 12.5, marginBottom: 8 }}>
            Included automatically — {pending.length} pending:{' '}
            {pending.map((c) => `${c.description} (${formatMoney(c.totalMinor, c.currency)})`).join('; ')}
          </div>
        ) : (
          customerId && (
            <div className="subtle" style={{ fontSize: 12.5, marginBottom: 8 }}>
              No pending charges for this client.
            </div>
          )
        )}
        {extras.map((row, i) => (
          <div key={i} className="row" style={{ gap: 6, marginBottom: 6 }}>
            <input
              className="input grow"
              placeholder="Description"
              value={row.description}
              onChange={(e) => setExtra(i, 'description', e.target.value)}
              aria-label="Line description"
            />
            <input
              className="input"
              style={{ width: 70 }}
              type="number"
              step="0.01"
              min="0.01"
              value={row.quantity}
              onChange={(e) => setExtra(i, 'quantity', e.target.value)}
              aria-label="Quantity"
            />
            <input
              className="input"
              style={{ width: 100 }}
              type="number"
              step="0.01"
              min="0"
              placeholder="Price"
              value={row.price}
              onChange={(e) => setExtra(i, 'price', e.target.value)}
              aria-label="Unit price"
            />
            <button type="button" className="btn btn-sm btn-icon" onClick={() => setExtras((rows) => rows.filter((_, j) => j !== i))} aria-label="Remove line">
              <Trash2 size={13} />
            </button>
          </div>
        ))}
        <button type="button" className="btn btn-sm" onClick={() => setExtras((rows) => [...rows, { description: '', quantity: '1', price: '' }])}>
          <Plus size={13} /> Add a line for this invoice
        </button>
      </div>

      <label className="row" style={{ gap: 8, marginBottom: 10, cursor: 'pointer', fontSize: 13 }}>
        <input type="checkbox" checked={includeUsage} onChange={(e) => setIncludeUsage(e.target.checked)} />
        Include metered call usage
      </label>

      <label className="row" style={{ gap: 8, marginBottom: 18, cursor: 'pointer', fontSize: 13 }}>
        <input type="checkbox" checked={autoSend} onChange={(e) => setAutoSend(e.target.checked)} />
        Issue and email to the client immediately
      </label>

      <button className="btn btn-primary" type="submit" disabled={busy || !customerId} style={{ width: '100%' }}>
        {busy ? 'Generating…' : autoSend ? 'Generate & send' : 'Generate draft'}
      </button>
    </form>
  );
}

export function InvoiceDetail() {
  const { id } = useParams<{ id: string }>();
  const { can } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [payOpen, setPayOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [dueDateOpen, setDueDateOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const { data, loading, error, reload } = useFetch(
    () =>
      api.get<Invoice & { lineItems: InvoiceLine[]; customerName: string; amountPaidMinor: number }>(`/api/billing/invoices/${id}`),
    [id],
  );
  const payments = useFetch(() => api.get<InvoicePayment[]>(`/api/billing/invoices/${id}/payments`), [id]);

  const setState = async (state: string) => {
    try {
      await api.post(`/api/billing/invoices/${id}/state`, { state });
      reload();
      toast(`Invoice marked ${state}`, 'good');
    } catch (err) {
      toast('Could not update the invoice', 'bad', err instanceof Error ? err.message : undefined);
    }
  };

  const remove = async () => {
    if (!data) return;
    const warning =
      data.state === 'draft'
        ? ''
        : `\n\nIt has been issued: the client may already have it, and ${data.number} will be missing from the invoice sequence. Void keeps the record instead.`;
    if (!confirm(`Delete invoice ${data.number}? This cannot be undone.${warning}`)) return;
    try {
      await api.delete(`/api/billing/invoices/${id}`);
      toast(`Invoice ${data.number} deleted`, 'good');
      navigate('/invoices');
    } catch (err) {
      toast('Could not delete the invoice', 'bad', err instanceof Error ? err.message : undefined);
    }
  };

  const send = async () => {
    setSending(true);
    try {
      const sent = await api.post<Invoice>(`/api/billing/invoices/${id}/send`);
      reload();
      if (sent.sendError) toast('Could not deliver the email', 'warn', sent.sendError);
      else toast('Invoice emailed to the client', 'good');
    } catch (err) {
      toast('Could not send the invoice', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setSending(false);
    }
  };

  if (loading && !data) return <SkeletonTable rows={6} cols={4} />;
  if (error) return <ErrorState error={error} onRetry={reload} />;
  if (!data) return <EmptyState title="Invoice not found" />;

  return (
    <div className="content-narrow" style={{ maxWidth: 860 }}>
      <div className="page-header">
        <div className="grow">
          <div className="row" style={{ gap: 8 }}>
            <span className="mono subtle">{data.number}</span>
            <Badge intent={autoIntent(data.state)}>{data.state}</Badge>
            <Badge>{data.currency}</Badge>
          </div>
          <h1 className="page-title" style={{ marginTop: 8 }}>
            {data.customerName}
          </h1>
          <div className="page-subtitle row" style={{ gap: 6 }}>
            <span>
              Service period {serviceRange(data.periodStart, data.periodEnd)}
              {data.dueAt ? ` · due ${formatRelative(data.dueAt)}` : ' · no fixed due date'}
            </span>
            {can('billing:write') && data.state !== 'void' && (
              <button className="btn btn-icon" onClick={() => setDueDateOpen(true)} title="Edit due date" aria-label="Edit due date">
                <Pencil size={12} />
              </button>
            )}
          </div>
          {data.state !== 'draft' && data.state !== 'void' && (
            <div className="page-subtitle" style={{ color: data.sendError ? 'var(--itr-critical)' : undefined }}>
              {data.sendError
                ? `Not delivered to client: ${data.sendError}`
                : data.sentAt
                  ? `Emailed to client ${formatRelative(data.sentAt)}`
                  : 'Not emailed to the client yet'}
            </div>
          )}
        </div>

        <div className="row">
          <button className="btn btn-icon" onClick={() => window.print()} title="Print" aria-label="Print">
            <Printer size={15} />
          </button>
          <button className="btn" onClick={() => setPreviewOpen(true)} title="Preview the email the client receives">
            <Eye size={14} /> Preview email
          </button>
          {can('billing:write') && (
            <>
              {data.state === 'draft' && (
                <button className="btn" onClick={() => setState('issued')}>
                  Issue
                </button>
              )}
              {data.state !== 'void' && data.state !== 'paid' && (
                <button className="btn" onClick={send} disabled={sending}>
                  <Send size={14} /> {sending ? 'Sending…' : data.sentAt ? 'Resend' : 'Issue & send'}
                </button>
              )}
              {(data.state === 'issued' || data.state === 'overdue') && (
                <button className="btn btn-primary" onClick={() => setPayOpen(true)}>
                  <CreditCard size={15} /> Record payment
                </button>
              )}
              {data.state !== 'void' && data.state !== 'paid' && (
                <button className="btn" onClick={() => setState('void')}>
                  Void
                </button>
              )}
              {data.state !== 'paid' && data.amountPaidMinor === 0 && (
                <button className="btn btn-danger" onClick={remove}>
                  <Trash2 size={14} /> Delete
                </button>
              )}
            </>
          )}
        </div>
      </div>

      <div className="card">
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Description</th>
                <th style={{ width: 90 }}>Type</th>
                <th data-align="right" style={{ width: 90 }}>
                  Qty
                </th>
                <th data-align="right" style={{ width: 110 }}>
                  Unit
                </th>
                <th data-align="right" style={{ width: 120 }}>
                  Amount
                </th>
              </tr>
            </thead>
            <tbody>
              {data.lineItems.length === 0 ? (
                <tr>
                  <td colSpan={5}>
                    <span className="subtle">No billable lines for this period.</span>
                  </td>
                </tr>
              ) : (
                data.lineItems.map((line) => (
                  <tr key={line.id}>
                    <td>
                      {line.description}
                      <div className="subtle" style={{ fontSize: 12 }}>
                        Service {serviceRange(line.serviceStart ?? data.periodStart, line.serviceEnd ?? data.periodEnd)}
                      </div>
                    </td>
                    <td>
                      <Badge>{line.kind}</Badge>
                    </td>
                    <td data-align="right" className="nums">
                      {line.quantity}
                    </td>
                    <td data-align="right" className="nums subtle">
                      {formatMoney(line.unitPriceMinor, data.currency)}
                    </td>
                    <td data-align="right" className="nums">
                      {formatMoney(line.totalMinor, data.currency)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="card-body" style={{ borderTop: 'var(--itr-border-w) solid var(--itr-border)' }}>
          <div className="col" style={{ gap: 6, marginLeft: 'auto', maxWidth: 280 }}>
            <Row label="Subtotal" value={formatMoney(data.subtotalMinor, data.currency)} />
            {data.taxMinor > 0 && <Row label="Tax" value={formatMoney(data.taxMinor, data.currency)} />}
            <div style={{ height: 1, background: 'var(--itr-border)', margin: '4px 0' }} />
            <Row label="Total" value={formatMoney(data.totalMinor, data.currency)} strong />
            {data.amountPaidMinor > 0 && (
              <>
                <Row label="Paid" value={formatMoney(data.amountPaidMinor, data.currency)} />
                <Row
                  label="Balance due"
                  value={formatMoney(Math.max(0, data.totalMinor - data.amountPaidMinor), data.currency)}
                  strong
                />
              </>
            )}
          </div>
        </div>
      </div>

      {(payments.data ?? []).length > 0 && (
        <div className="card" style={{ marginTop: 14 }}>
          <div className="card-header">
            <Receipt size={14} />
            <span className="card-title">Payment history</span>
          </div>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Method</th>
                  <th>Reference</th>
                  <th data-align="right" style={{ width: 120 }}>
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody>
                {(payments.data ?? []).map((p) => (
                  <tr key={p.id}>
                    <td className="subtle" style={{ fontSize: 12.5 }}>
                      {new Date(p.createdAt).toLocaleString()}
                    </td>
                    <td>
                      <Badge>{p.method.replace('_', ' ')}</Badge>
                    </td>
                    <td className="subtle truncate" style={{ maxWidth: 220, fontSize: 12.5 }}>
                      {[p.reference, p.note].filter(Boolean).join(' — ') || '—'}
                    </td>
                    <td data-align="right" className="nums">
                      {formatMoney(p.amountMinor, data.currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Drawer open={payOpen} onClose={() => setPayOpen(false)} title="Record a payment">
        <RecordPaymentForm
          invoice={data}
          onSaved={() => {
            setPayOpen(false);
            reload();
            payments.reload();
            toast('Payment recorded', 'good');
          }}
        />
      </Drawer>

      <Drawer open={previewOpen} onClose={() => setPreviewOpen(false)} title={`Email preview — ${data.number}`} wide>
        <InvoiceEmailPreview invoiceId={data.id} />
      </Drawer>

      <Drawer open={dueDateOpen} onClose={() => setDueDateOpen(false)} title="Edit due date">
        <DueDateForm
          invoice={data}
          onSaved={() => {
            setDueDateOpen(false);
            reload();
            toast('Due date updated', 'good');
          }}
        />
      </Drawer>
    </div>
  );
}

function DueDateForm({ invoice, onSaved }: { invoice: Invoice; onSaved: () => void }) {
  const toast = useToast();
  const [dueDate, setDueDate] = useState(invoice.dueAt ? isoDate(invoice.dueAt) : '');
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/api/billing/invoices/${invoice.id}/due-date`, { dueAt: dueDate ? Date.parse(dueDate) : null });
      onSaved();
    } catch (err) {
      toast('Could not update the due date', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <div className="field">
        <label className="field-label">Due date</label>
        <input className="input" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
        <span className="field-help">Leave blank for no fixed due date.</span>
      </div>
      <button className="btn btn-primary" type="submit" disabled={busy} style={{ width: '100%' }}>
        {busy ? 'Saving…' : 'Save due date'}
      </button>
    </form>
  );
}

function RecordPaymentForm({
  invoice,
  onSaved,
}: {
  invoice: Invoice & { amountPaidMinor: number };
  onSaved: () => void;
}) {
  const toast = useToast();
  const remaining = Math.max(0, invoice.totalMinor - invoice.amountPaidMinor);
  const [amount, setAmount] = useState(String(remaining / 100));
  const [method, setMethod] = useState<PaymentMethod>('bank_transfer');
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const amountMinor = Math.round(Number(amount) * 100);
    if (!Number.isFinite(amountMinor) || amountMinor <= 0) {
      toast('Enter a valid amount', 'bad');
      return;
    }
    setBusy(true);
    try {
      await api.post(`/api/billing/invoices/${invoice.id}/payments`, {
        amountMinor,
        method,
        reference: reference.trim() || null,
        note: note.trim() || null,
      });
      onSaved();
    } catch (err) {
      toast('Could not record the payment', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <p className="subtle" style={{ fontSize: 13, marginBottom: 16 }}>
        Balance due: {formatMoney(remaining, invoice.currency)}. A partial amount is fine — the invoice only moves to
        "paid" once payments cover the full total.
      </p>
      <div className="field">
        <label className="field-label">Amount ({invoice.currency})</label>
        <input
          className="input"
          type="number"
          min={0.01}
          step={0.01}
          required
          autoFocus
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
        />
      </div>
      <div className="field">
        <label className="field-label">Method</label>
        <select className="select" value={method} onChange={(e) => setMethod(e.target.value as PaymentMethod)}>
          {PAYMENT_METHODS.map((m) => (
            <option key={m} value={m}>
              {m.replace('_', ' ')}
            </option>
          ))}
        </select>
      </div>
      <div className="field">
        <label className="field-label">Reference</label>
        <input className="input" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Transaction/cheque number" />
      </div>
      <div className="field">
        <label className="field-label">Note</label>
        <textarea className="input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
      </div>
      <button className="btn btn-primary" type="submit" disabled={busy} style={{ width: '100%' }}>
        {busy ? 'Recording…' : 'Record payment'}
      </button>
    </form>
  );
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="row" style={{ fontSize: strong ? 15 : 13 }}>
      <span className={strong ? undefined : 'subtle'}>{label}</span>
      <span className="grow" />
      <span className="nums" style={{ fontWeight: strong ? 660 : 550 }}>
        {value}
      </span>
    </div>
  );
}

/* ================================================================== */
/* Subscriptions                                                      */
/* ================================================================== */

/** The suspension / overdue-chasing options: enable, the reminder schedule, the
 * suspend threshold, and the net terms on auto-generated renewal invoices. */
function DunningSettingsCard() {
  const toast = useToast();
  const settings = useFetch(
    () => api.get<{ dunning: { enabled: boolean; reminderDays: number[]; suspendDays: number; renewalNetDays: number } }>('/api/billing/settings'),
    [],
  );
  const [enabled, setEnabled] = useState(true);
  const [reminderDays, setReminderDays] = useState('0, 3, 7');
  const [suspendDays, setSuspendDays] = useState('10');
  const [netDays, setNetDays] = useState('10');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const d = settings.data?.dunning;
    if (!d) return;
    setEnabled(d.enabled);
    setReminderDays(d.reminderDays.join(', '));
    setSuspendDays(String(d.suspendDays));
    setNetDays(String(d.renewalNetDays));
  }, [settings.data]);

  const save = async () => {
    setBusy(true);
    try {
      const parsed = reminderDays
        .split(',')
        .map((s) => parseInt(s.trim(), 10))
        .filter((n) => Number.isFinite(n) && n >= 0);
      await api.put('/api/billing/dunning-settings', {
        enabled,
        reminderDays: parsed.length ? parsed : [0, 3, 7],
        suspendDays: Math.max(0, parseInt(suspendDays, 10) || 0),
        renewalNetDays: Math.max(0, parseInt(netDays, 10) || 0),
      });
      toast('Dunning settings saved', 'good');
      settings.reload();
    } catch (err) {
      toast('Could not save dunning settings', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card" style={{ marginBottom: 12 }}>
      <div className="card-header">
        <span className="card-title">Overdue chasing &amp; suspension</span>
        <span className="grow" />
        <label className="row" style={{ gap: 6, fontSize: 12.5 }}>
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} /> Enabled
        </label>
      </div>
      <div className="card-body">
        <div className="row" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="field" style={{ minWidth: 170 }}>
            <label className="field-label">Reminders (days overdue)</label>
            <input className="input" value={reminderDays} onChange={(e) => setReminderDays(e.target.value)} placeholder="0, 3, 7" />
            <span className="field-help">Email the customer on each of these days past due.</span>
          </div>
          <div className="field" style={{ width: 150 }}>
            <label className="field-label">Suspend at (days overdue)</label>
            <input className="input" type="number" min={0} value={suspendDays} onChange={(e) => setSuspendDays(e.target.value)} />
            <span className="field-help">Service is paused once this is reached.</span>
          </div>
          <div className="field" style={{ width: 140 }}>
            <label className="field-label">Renewal net (days)</label>
            <input className="input" type="number" min={0} value={netDays} onChange={(e) => setNetDays(e.target.value)} />
            <span className="field-help">Due date on auto invoices.</span>
          </div>
          <button className="btn btn-primary" onClick={save} disabled={busy}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

function SubscriptionsTab({ canWrite }: { canWrite: boolean }) {
  const toast = useToast();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<SubRow | null>(null);
  const [generatingFor, setGeneratingFor] = useState<SubRow | null>(null);

  const subs = useFetch(() => api.get<SubRow[]>('/api/billing/subscriptions'), []);
  const plans = useFetch(() => api.get<SubscriptionPlan[]>('/api/billing/plans', { activeOnly: true }), []);
  const customers = useFetch(() => api.get<Array<{ id: string; name: string }>>('/api/customers'), []);

  return (
    <>
      {canWrite && <DunningSettingsCard />}
      {canWrite && (
        <div className="row" style={{ marginBottom: 12 }}>
          <button
            className="btn"
            onClick={async () => {
              const result = await api.post<{ renewed: number; charged: number; cancelled: number; invoiced: number }>(
                '/api/billing/subscriptions/run-renewals',
              );
              subs.reload();
              toast(`${result.renewed} renewed, ${result.charged} charged, ${result.invoiced} invoiced, ${result.cancelled} cancelled`, 'good');
            }}
          >
            <RefreshCw size={15} /> Run renewals now
          </button>
          <button
            className="btn"
            title="Send overdue reminders and suspend services past the threshold, now"
            onClick={async () => {
              const r = await api.post<{ reminded: number; suspended: number }>('/api/billing/dunning/run');
              subs.reload();
              toast(`${r.reminded} reminded, ${r.suspended} suspended`, 'good');
            }}
          >
            <Send size={15} /> Run dunning now
          </button>
          <span className="grow" />
          <button className="btn btn-primary" onClick={() => setCreating(true)}>
            <Plus size={15} /> New subscription
          </button>
        </div>
      )}

      <div className="card">
        {subs.loading ? (
          <div style={{ padding: 16 }}>
            <SkeletonTable rows={5} cols={5} />
          </div>
        ) : (subs.data ?? []).length === 0 ? (
          <EmptyState icon={<Repeat size={20} />} title="No subscriptions" text="Put a customer on a recurring plan to bill them automatically." />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Plan</th>
                  <th data-align="right" style={{ width: 70 }}>
                    Qty
                  </th>
                  <th style={{ width: 100 }}>Status</th>
                  <th style={{ width: 130 }}>Renews</th>
                  <th style={{ width: 210 }} />
                </tr>
              </thead>
              <tbody>
                {(subs.data ?? []).map((sub) => (
                  <tr key={sub.id}>
                    <td style={{ fontWeight: 550 }}>{sub.customerName}</td>
                    <td>
                      {sub.planName}
                      <span className="subtle" style={{ fontSize: 11.5, marginLeft: 6 }}>
                        {sub.interval} · {sub.currency}
                      </span>
                    </td>
                    <td data-align="right" className="nums">
                      {sub.quantity}
                    </td>
                    <td>
                      <Badge intent={autoIntent(sub.status)}>{sub.status}</Badge>
                    </td>
                    <td className="subtle" style={{ fontSize: 12 }}>
                      {sub.cancelAt ? `ends ${isoDate(sub.cancelAt)}` : formatRelative(sub.periodEnd)}
                    </td>
                    <td>
                      <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                        {canWrite && (
                          <button className="btn btn-sm" onClick={() => setGeneratingFor(sub)} title="Generate an invoice for this subscription's customer and current period">
                            <FileText size={12} /> Invoice
                          </button>
                        )}
                        {canWrite && (
                          <button className="btn btn-sm" onClick={() => setEditing(sub)}>
                            Edit
                          </button>
                        )}
                        {canWrite && sub.status === 'paused' && (
                          <button
                            className="btn btn-sm"
                            title="Resume this service (reactivate the subscription)"
                            onClick={async () => {
                              await api.post(`/api/billing/subscriptions/${sub.id}/resume`);
                              subs.reload();
                              toast('Service resumed', 'good');
                            }}
                          >
                            Resume
                          </button>
                        )}
                        {canWrite && sub.status !== 'cancelled' && sub.status !== 'paused' && (
                          <button
                            className="btn btn-sm"
                            title="Suspend this service now (pause the subscription)"
                            onClick={async () => {
                              if (!confirm('Suspend this service now?\n\nThe subscription will be paused. Dunning resumes it automatically when the overdue invoice is paid.')) return;
                              await api.post(`/api/billing/subscriptions/${sub.id}/suspend`);
                              subs.reload();
                              toast('Service suspended', 'warn');
                            }}
                          >
                            Suspend
                          </button>
                        )}
                        {canWrite && sub.status !== 'cancelled' && (
                          <button
                            className="btn btn-sm"
                            onClick={async () => {
                              const immediately = confirm(
                                'Cancel immediately?\n\nOK = end now.\nCancel = end when the paid period runs out (recommended — they have already paid for it).',
                              );
                              await api.post(`/api/billing/subscriptions/${sub.id}/cancel`, { immediately });
                              subs.reload();
                              toast('Subscription cancelled', 'good');
                            }}
                          >
                            Cancel
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Drawer open={creating} onClose={() => setCreating(false)} title="New subscription">
        <SubscriptionForm
          plans={plans.data ?? []}
          customers={customers.data ?? []}
          onSaved={() => {
            setCreating(false);
            subs.reload();
            toast('Subscription created', 'good');
          }}
        />
      </Drawer>

      <Drawer
        open={Boolean(generatingFor)}
        onClose={() => setGeneratingFor(null)}
        title={generatingFor ? `Generate invoice — ${generatingFor.customerName}` : ''}
      >
        {generatingFor && (
          <GenerateForm
            initial={{
              customerId: generatingFor.customerId,
              periodStart: generatingFor.periodStart,
              periodEnd: generatingFor.periodEnd,
              currency: generatingFor.currency,
            }}
            onDone={(invoice) => {
              setGeneratingFor(null);
              if (invoice.state === 'draft') toast('Invoice generated as a draft', 'good');
              else if (invoice.sendError) toast('Invoice issued, but the email could not be delivered', 'warn', invoice.sendError);
              else toast('Invoice issued and emailed to the client', 'good');
              navigate(`/invoices/${invoice.id}`);
            }}
          />
        )}
      </Drawer>

      <Drawer open={Boolean(editing)} onClose={() => setEditing(null)} title={editing ? `Edit — ${editing.customerName}` : ''}>
        {editing && (
          <EditSubscriptionForm
            subscription={editing}
            onSaved={() => {
              setEditing(null);
              subs.reload();
              toast('Subscription updated', 'good');
            }}
          />
        )}
      </Drawer>
    </>
  );
}

export function SubscriptionForm({
  plans,
  customers,
  fixedCustomerId,
  onSaved,
}: {
  plans: SubscriptionPlan[];
  customers: Array<{ id: string; name: string }>;
  /** Locks the customer field, for adding a plan from that client's own page. */
  fixedCustomerId?: string;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [customerId, setCustomerId] = useState(fixedCustomerId ?? '');
  const [planId, setPlanId] = useState('');
  const [quantity, setQuantity] = useState('1');
  const [priceMinor, setPriceMinor] = useState('');
  // Blank bills in the plan's currency; another one needs its own price.
  const [currency, setCurrency] = useState('');
  const [busy, setBusy] = useState(false);

  const plan = plans.find((p) => p.id === planId);
  const billedIn = currency || plan?.currency || 'USD';
  const needsPrice = Boolean(plan && currency && currency !== plan.currency);
  const unit = priceMinor ? Math.round(Number(priceMinor) * 100) : (plan?.priceMinor ?? 0);
  const total = unit * (Number(quantity) || 1);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/api/billing/subscriptions', {
        customerId,
        planId,
        quantity: Number(quantity) || 1,
        priceMinor: priceMinor ? Math.round(Number(priceMinor) * 100) : null,
        ...(currency ? { currency } : {}),
      });
      onSaved();
    } catch (err) {
      toast('Could not create the subscription', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit}>
      {!fixedCustomerId && (
        <div className="field">
          <label className="field-label">Customer</label>
          <select className="select" required value={customerId} onChange={(e) => setCustomerId(e.target.value)}>
            <option value="">Select…</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="field">
        <label className="field-label">Plan</label>
        <select className="select" required value={planId} onChange={(e) => setPlanId(e.target.value)}>
          <option value="">Select…</option>
          {plans.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name} — {formatMoney(p.priceMinor, p.currency)} / {p.unitLabel} / {p.interval}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <label className="field-label">Quantity {plan ? `(${plan.unitLabel}s)` : ''}</label>
        <input className="input" type="number" min={1} value={quantity} onChange={(e) => setQuantity(e.target.value)} />
      </div>

      <div className="row" style={{ gap: 8 }}>
        <div className="field grow">
          <label className="field-label">Negotiated unit price {needsPrice ? `(${currency})` : '(optional)'}</label>
          <input
            className="input"
            type="number"
            step="0.01"
            min="0"
            required={needsPrice}
            value={priceMinor}
            onChange={(e) => setPriceMinor(e.target.value)}
            placeholder={plan && !needsPrice ? String(plan.priceMinor / 100) : ''}
          />
          <span className="field-help">
            {needsPrice ? `Required — the plan is priced in ${plan!.currency}.` : 'Leave blank to use the plan price.'}
          </span>
        </div>
        <CurrencyField value={currency} onChange={setCurrency} placeholder={plan?.currency ?? 'Plan'} />
      </div>

      {plan && (
        <div className="card" style={{ padding: 12, marginBottom: 16 }}>
          <div className="row" style={{ fontSize: 13 }}>
            <span className="subtle">Charged every {plan.interval.replace('ly', '')}</span>
            <span className="grow" />
            <span className="nums" style={{ fontWeight: 620 }}>
              {formatMoney(total, billedIn)}
            </span>
          </div>
        </div>
      )}

      <button className="btn btn-primary" type="submit" disabled={busy || !customerId || !planId} style={{ width: '100%' }}>
        {busy ? 'Creating…' : 'Create subscription'}
      </button>
    </form>
  );
}

/** Edits an existing subscription — quantity, price, status and every date on it. */
export function EditSubscriptionForm({ subscription, onSaved }: { subscription: Subscription; onSaved: () => void }) {
  const toast = useToast();
  const [quantity, setQuantity] = useState(String(subscription.quantity));
  const [priceMinor, setPriceMinor] = useState(subscription.priceMinor !== null ? String(subscription.priceMinor / 100) : '');
  const [currency, setCurrency] = useState(subscription.currency);
  const [status, setStatus] = useState<SubscriptionStatus>(subscription.status);
  const [notes, setNotes] = useState(subscription.notes ?? '');
  const [startedAt, setStartedAt] = useState(isoDate(subscription.startedAt));
  const [periodStart, setPeriodStart] = useState(isoDate(subscription.periodStart));
  const [periodEnd, setPeriodEnd] = useState(isoDate(subscription.periodEnd));
  const [cancelAt, setCancelAt] = useState(subscription.cancelAt ? isoDate(subscription.cancelAt) : '');
  const [trialEndsAt, setTrialEndsAt] = useState(subscription.trialEndsAt ? isoDate(subscription.trialEndsAt) : '');
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (Date.parse(periodEnd) <= Date.parse(periodStart)) {
      toast('Period end must be after period start', 'bad');
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/api/billing/subscriptions/${subscription.id}`, {
        quantity: Number(quantity) || 1,
        priceMinor: priceMinor ? Math.round(Number(priceMinor) * 100) : null,
        currency,
        status,
        notes: notes.trim() || null,
        startedAt: Date.parse(startedAt),
        periodStart: Date.parse(periodStart),
        periodEnd: Date.parse(periodEnd),
        cancelAt: cancelAt ? Date.parse(cancelAt) : null,
        trialEndsAt: trialEndsAt ? Date.parse(trialEndsAt) : null,
      });
      onSaved();
    } catch (err) {
      toast('Could not update the subscription', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <div className="row" style={{ gap: 8 }}>
        <div className="field grow">
          <label className="field-label">Quantity</label>
          <input className="input" type="number" min={1} value={quantity} onChange={(e) => setQuantity(e.target.value)} />
        </div>
        <div className="field grow">
          <label className="field-label">Negotiated unit price</label>
          <input
            className="input"
            type="number"
            step="0.01"
            min="0"
            value={priceMinor}
            onChange={(e) => setPriceMinor(e.target.value)}
            placeholder="Plan price"
          />
        </div>
        <CurrencyField value={currency} onChange={setCurrency} required />
      </div>
      <span className="field-help" style={{ marginTop: -10, display: 'block', marginBottom: 14 }}>
        Billed in {currency || '—'}. A currency other than the plan's needs its own price. Invoices already issued keep
        theirs.
      </span>

      <div className="field">
        <label className="field-label">Status</label>
        <select className="select" value={status} onChange={(e) => setStatus(e.target.value as SubscriptionStatus)}>
          {SUBSCRIPTION_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      <div className="row" style={{ gap: 8 }}>
        <div className="field grow">
          <label className="field-label">Started</label>
          <input className="input" type="date" required value={startedAt} onChange={(e) => setStartedAt(e.target.value)} />
        </div>
        <div className="field grow">
          <label className="field-label">Trial ends</label>
          <input className="input" type="date" value={trialEndsAt} onChange={(e) => setTrialEndsAt(e.target.value)} />
        </div>
      </div>

      <div className="row" style={{ gap: 8 }}>
        <div className="field grow">
          <label className="field-label">Current period start</label>
          <input className="input" type="date" required value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} />
        </div>
        <div className="field grow">
          <label className="field-label">Current period end</label>
          <input className="input" type="date" required value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} />
        </div>
      </div>
      <span className="field-help" style={{ marginTop: -10, display: 'block', marginBottom: 14 }}>
        This is the billing period the customer is currently paying for — it drives the next renewal/charge date.
      </span>

      <div className="field">
        <label className="field-label">Cancel at (scheduled end)</label>
        <input className="input" type="date" value={cancelAt} onChange={(e) => setCancelAt(e.target.value)} />
        <span className="field-help">Leave blank for an open-ended subscription.</span>
      </div>

      <div className="field">
        <label className="field-label">Notes</label>
        <textarea className="input" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
      </div>

      <button className="btn btn-primary" type="submit" disabled={busy} style={{ width: '100%' }}>
        {busy ? 'Saving…' : 'Save changes'}
      </button>
    </form>
  );
}

/* ================================================================== */
/* Plans                                                              */
/* ================================================================== */

function PlansTab({ canWrite }: { canWrite: boolean }) {
  const toast = useToast();
  const [editing, setEditing] = useState<SubscriptionPlan | null>(null);
  const [creating, setCreating] = useState(false);
  const plans = useFetch(() => api.get<SubscriptionPlan[]>('/api/billing/plans'), []);

  return (
    <>
      {canWrite && (
        <div className="row" style={{ marginBottom: 12 }}>
          <span className="grow" />
          <button className="btn btn-primary" onClick={() => setCreating(true)}>
            <Plus size={15} /> New plan
          </button>
        </div>
      )}

      {(plans.data ?? []).length === 0 ? (
        <EmptyState icon={<Receipt size={20} />} title="No plans" text="A plan defines a recurring price and what one unit of it is." />
      ) : (
        <div className="grid-auto">
          {(plans.data ?? []).map((plan) => (
            <div className="card" key={plan.id} style={{ opacity: plan.active ? 1 : 0.55 }}>
              <div className="card-header">
                <div className="grow" style={{ minWidth: 0 }}>
                  <div className="card-title truncate">{plan.name}</div>
                  <div className="card-subtitle">
                    {formatMoney(plan.priceMinor, plan.currency)} / {plan.unitLabel} / {plan.interval}
                  </div>
                </div>
                {!plan.active && <Badge>inactive</Badge>}
              </div>
              <div className="card-body">
                <p className="subtle" style={{ fontSize: 12.5, minHeight: 30 }}>
                  {plan.description || '—'}
                </p>
                {canWrite && (
                  <button className="btn btn-sm" onClick={() => setEditing(plan)} style={{ marginTop: 8 }}>
                    Edit
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Drawer
        open={creating || Boolean(editing)}
        onClose={() => {
          setCreating(false);
          setEditing(null);
        }}
        title={editing ? `Edit ${editing.name}` : 'New plan'}
      >
        <PlanForm
          plan={editing}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            plans.reload();
            toast('Plan saved', 'good');
          }}
        />
      </Drawer>
    </>
  );
}

function PlanForm({ plan, onSaved }: { plan: SubscriptionPlan | null; onSaved: () => void }) {
  const toast = useToast();
  const [form, setForm] = useState({
    name: plan?.name ?? '',
    description: plan?.description ?? '',
    price: plan ? String(plan.priceMinor / 100) : '',
    interval: plan?.interval ?? 'monthly',
    unitLabel: plan?.unitLabel ?? 'subscription',
    currency: plan?.currency ?? 'USD',
    active: plan?.active ?? true,
  });
  const [busy, setBusy] = useState(false);

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/api/billing/plans', {
        ...(plan ? { id: plan.id } : {}),
        name: form.name.trim(),
        description: form.description,
        priceMinor: Math.round(Number(form.price) * 100) || 0,
        interval: form.interval,
        unitLabel: form.unitLabel,
        currency: form.currency,
        active: form.active,
      });
      onSaved();
    } catch (err) {
      toast('Save failed', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <div className="field">
        <label className="field-label">Name</label>
        <input className="input" required autoFocus value={form.name} onChange={set('name')} placeholder="Managed VOIP — Standard" />
      </div>
      <div className="field">
        <label className="field-label">Description</label>
        <input className="input" value={form.description} onChange={set('description')} />
      </div>
      <div className="row" style={{ gap: 8 }}>
        <div className="field grow">
          <label className="field-label">Price</label>
          <input className="input" type="number" step="0.01" min="0" required value={form.price} onChange={set('price')} />
        </div>
        <div className="field" style={{ width: 110 }}>
          <label className="field-label">Currency</label>
          <input className="input" maxLength={3} value={form.currency} onChange={set('currency')} />
        </div>
      </div>
      <div className="row" style={{ gap: 8 }}>
        <div className="field grow">
          <label className="field-label">Billed</label>
          <select className="select" value={form.interval} onChange={set('interval')}>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
            <option value="yearly">Yearly</option>
          </select>
        </div>
        <div className="field grow">
          <label className="field-label">Unit</label>
          <input className="input" value={form.unitLabel} onChange={set('unitLabel')} placeholder="channel, seat, device" />
        </div>
      </div>
      <label className="row" style={{ gap: 8, marginBottom: 16, cursor: 'pointer', fontSize: 13 }}>
        <input type="checkbox" checked={form.active} onChange={set('active')} />
        Available for new subscriptions
      </label>
      <button className="btn btn-primary" type="submit" disabled={busy} style={{ width: '100%' }}>
        {busy ? 'Saving…' : 'Save plan'}
      </button>
    </form>
  );
}

/* ================================================================== */
/* Invoice templates ("letter format")                                */
/* ================================================================== */

function TemplatesTab({ canWrite }: { canWrite: boolean }) {
  const toast = useToast();
  const [editing, setEditing] = useState<InvoiceTemplate | null>(null);
  const [creating, setCreating] = useState(false);
  const [previewing, setPreviewing] = useState<InvoiceTemplate | null>(null);
  const templates = useFetch(() => api.get<InvoiceTemplate[]>('/api/billing/invoice-templates'), []);

  const remove = async (t: InvoiceTemplate) => {
    if (!confirm(`Delete the "${t.name}" template? Invoices already sent with it keep their copy; new sends fall back to whatever is default.`)) return;
    await api.delete(`/api/billing/invoice-templates/${t.id}`);
    templates.reload();
    toast('Template deleted', 'good');
  };

  return (
    <>
      <p className="subtle" style={{ fontSize: 13, marginBottom: 12, maxWidth: 640 }}>
        The letterhead — company identity, terms and footer — applied when an invoice is rendered and emailed to a
        client. Pick one per invoice at generation time, or leave the default in place.
      </p>
      {canWrite && (
        <div className="row" style={{ marginBottom: 12 }}>
          <span className="grow" />
          <button className="btn btn-primary" onClick={() => setCreating(true)}>
            <Plus size={15} /> New template
          </button>
        </div>
      )}

      {(templates.data ?? []).length === 0 ? (
        <EmptyState
          icon={<Mail size={20} />}
          title="No invoice templates"
          text="Without one, invoices are sent with a generic placeholder letterhead."
        />
      ) : (
        <div className="grid-auto">
          {(templates.data ?? []).map((t) => (
            <div className="card" key={t.id}>
              <div className="card-header">
                <div className="grow" style={{ minWidth: 0 }}>
                  <div className="card-title truncate">{t.name}</div>
                  <div className="card-subtitle truncate">{t.companyName || '—'}</div>
                </div>
                {t.isDefault && <Badge intent="good">default</Badge>}
              </div>
              <div className="card-body">
                <p className="subtle truncate" style={{ fontSize: 12.5, minHeight: 18 }}>
                  {t.companyEmail || t.companyPhone || 'No contact details set'}
                </p>
                <div className="row" style={{ gap: 6, marginTop: 8 }}>
                  <button className="btn btn-sm" onClick={() => setPreviewing(t)}>
                    <Eye size={12} /> Preview
                  </button>
                  {canWrite && (
                    <>
                      <button className="btn btn-sm" onClick={() => setEditing(t)}>
                        Edit
                      </button>
                      <button className="btn btn-sm btn-danger" onClick={() => remove(t)}>
                        <Trash2 size={12} />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Drawer
        open={creating || Boolean(editing)}
        onClose={() => {
          setCreating(false);
          setEditing(null);
        }}
        title={editing ? `Edit — ${editing.name}` : 'New invoice template'}
        wide
      >
        <TemplateForm
          template={editing}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            templates.reload();
            toast('Template saved', 'good');
          }}
        />
      </Drawer>

      <Drawer open={Boolean(previewing)} onClose={() => setPreviewing(null)} title={previewing ? `Preview — ${previewing.name}` : ''} wide>
        {previewing && <InvoiceEmailPreview template={previewing} />}
      </Drawer>
    </>
  );
}

/**
 * The invoice email exactly as the server renders it for sending — a template
 * against sample data, or a real invoice with its own letterhead. Sandboxed
 * with scripts off; same-origin is kept only so the frame can size to fit.
 */
function InvoiceEmailPreview({ template, invoiceId }: { template?: Partial<InvoiceTemplate>; invoiceId?: string }) {
  const body = useDebounced(JSON.stringify({ template: template ? templatePreviewFields(template) : null, invoiceId: invoiceId ?? null }), 400);
  const { data, error } = useFetch(() => api.post<{ subject: string; html: string }>('/api/billing/invoice-templates/preview', JSON.parse(body)), [body]);
  const [height, setHeight] = useState(600);

  if (error) return <ErrorState error={error} />;
  if (!data) return <SkeletonTable rows={6} cols={3} />;

  return (
    <>
      <div className="subtle" style={{ fontSize: 12.5, marginBottom: 8 }}>
        {invoiceId ? 'Subject' : 'Subject (sample invoice)'}: <strong>{data.subject}</strong>
      </div>
      <iframe
        title="Invoice email preview"
        sandbox="allow-same-origin"
        srcDoc={data.html}
        onLoad={(e) => {
          const doc = e.currentTarget.contentDocument;
          if (doc) setHeight(doc.documentElement.scrollHeight);
        }}
        style={{ width: '100%', height, border: '1px solid var(--itr-border)', borderRadius: 8, background: '#f1f5f9' }}
      />
    </>
  );
}

function templatePreviewFields(t: Partial<InvoiceTemplate>) {
  return {
    companyName: t.companyName ?? '',
    companyAddress: t.companyAddress ?? '',
    companyEmail: t.companyEmail ?? '',
    companyPhone: t.companyPhone ?? '',
    logoUrl: t.logoUrl?.trim() || null,
    accentColor: t.accentColor || '#2563eb',
    footerNote: t.footerNote ?? '',
    terms: t.terms ?? '',
  };
}

function TemplateForm({ template, onSaved }: { template: InvoiceTemplate | null; onSaved: () => void }) {
  const toast = useToast();
  const [form, setForm] = useState({
    name: template?.name ?? '',
    isDefault: template?.isDefault ?? false,
    companyName: template?.companyName ?? '',
    companyAddress: template?.companyAddress ?? '',
    companyEmail: template?.companyEmail ?? '',
    companyPhone: template?.companyPhone ?? '',
    logoUrl: template?.logoUrl ?? '',
    accentColor: template?.accentColor ?? '#2563eb',
    footerNote: template?.footerNote ?? '',
    terms: template?.terms ?? '',
  });
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState<'details' | 'preview'>('details');

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post('/api/billing/invoice-templates', {
        ...(template ? { id: template.id } : {}),
        name: form.name.trim(),
        isDefault: form.isDefault,
        companyName: form.companyName.trim(),
        companyAddress: form.companyAddress,
        companyEmail: form.companyEmail.trim(),
        companyPhone: form.companyPhone.trim(),
        logoUrl: form.logoUrl.trim() || null,
        accentColor: form.accentColor,
        footerNote: form.footerNote,
        terms: form.terms,
      });
      onSaved();
    } catch (err) {
      toast('Save failed', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  return (
    // A required field hidden behind the preview would block submit silently — flip back so it is visible.
    <form onSubmit={submit} onInvalidCapture={() => setView('details')}>
      <div className="row" style={{ gap: 4, marginBottom: 16 }}>
        <button type="button" className={`btn btn-sm${view === 'details' ? ' btn-primary' : ''}`} onClick={() => setView('details')}>
          <Pencil size={12} /> Details
        </button>
        <button type="button" className={`btn btn-sm${view === 'preview' ? ' btn-primary' : ''}`} onClick={() => setView('preview')}>
          <Eye size={12} /> Preview
        </button>
      </div>

      {view === 'preview' && (
        <div style={{ marginBottom: 16 }}>
          <InvoiceEmailPreview template={form} />
        </div>
      )}

      <div style={{ display: view === 'details' ? undefined : 'none' }}>
        <div className="field">
          <label className="field-label">Template name</label>
          <input className="input" required autoFocus value={form.name} onChange={set('name')} placeholder="Standard invoice" />
        </div>

        <div className="row" style={{ gap: 8 }}>
          <div className="field grow">
            <label className="field-label">Company name</label>
            <input className="input" value={form.companyName} onChange={set('companyName')} placeholder="IT Resources" />
          </div>
          <div className="field" style={{ width: 90 }}>
            <label className="field-label">Accent</label>
            <input className="input" type="color" value={form.accentColor} onChange={set('accentColor')} style={{ padding: 3, height: 36 }} />
          </div>
        </div>

        <div className="field">
          <label className="field-label">Company address</label>
          <textarea className="input" rows={2} value={form.companyAddress} onChange={set('companyAddress')} />
        </div>

        <div className="row" style={{ gap: 8 }}>
          <div className="field grow">
            <label className="field-label">Contact email</label>
            <input className="input" type="email" value={form.companyEmail} onChange={set('companyEmail')} />
          </div>
          <div className="field grow">
            <label className="field-label">Contact phone</label>
            <input className="input" value={form.companyPhone} onChange={set('companyPhone')} />
          </div>
        </div>

        <div className="field">
          <label className="field-label">Logo URL</label>
          <input className="input" value={form.logoUrl} onChange={set('logoUrl')} placeholder="https://…/logo.png" />
          <span className="field-help">Optional — shown at the top of the emailed invoice.</span>
        </div>

        <div className="field">
          <label className="field-label">Terms</label>
          <textarea className="input" rows={3} value={form.terms} onChange={set('terms')} placeholder="Payment due within 30 days…" />
        </div>

        <div className="field">
          <label className="field-label">Footer note</label>
          <textarea className="input" rows={2} value={form.footerNote} onChange={set('footerNote')} placeholder="Thank you for your business." />
        </div>

        <label className="row" style={{ gap: 8, marginBottom: 16, cursor: 'pointer', fontSize: 13 }}>
          <input type="checkbox" checked={form.isDefault} onChange={set('isDefault')} />
          Use as the default template
        </label>
      </div>

      <button className="btn btn-primary" type="submit" disabled={busy} style={{ width: '100%' }}>
        {busy ? 'Saving…' : 'Save template'}
      </button>
    </form>
  );
}

function isoDate(ts: number): string {
  return new Date(ts).toISOString().slice(0, 10);
}

/** "2026-08-01 – 2026-08-31", or a single date for a one-off — same rule as the emailed invoice. */
function serviceRange(start: number, end: number): string {
  const from = isoDate(start);
  const to = isoDate(serviceEndForDisplay(start, end));
  return from === to ? from : `${from} – ${to}`;
}

function CurrencyField({
  value,
  onChange,
  placeholder,
  required,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
}) {
  return (
    <div className="field" style={{ width: 110 }}>
      <label className="field-label">Currency</label>
      <input
        className="input"
        maxLength={3}
        pattern="[A-Za-z]{3}"
        title="Three-letter currency code, e.g. USD"
        required={required}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value.toUpperCase())}
      />
    </div>
  );
}
